'use client';

import React, { useEffect, useRef } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverAnchor,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { SlashCommand } from '@/lib/slash-commands/registry';

interface SlashCommandMenuProps {
  isOpen: boolean;
  targetElement: HTMLElement | null;
  commands: SlashCommand[];
  selectedIndex: number;
  onSelectCommand: (commandId: string) => void; // Callback to execute command
  onClose: () => void;
  onHoverIndex: (index: number) => void; // Callback to update index on hover
}

export function SlashCommandMenu({
  isOpen,
  targetElement,
  commands,
  selectedIndex,
  onSelectCommand,
  onClose,
  onHoverIndex,
}: SlashCommandMenuProps) {
  const listRef = useRef<HTMLDivElement>(null);

  // Scroll selected item into view
  useEffect(() => {
    if (isOpen && listRef.current) {
      const selectedElement = listRef.current.children[
        selectedIndex
      ] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({
          block: 'nearest',
        });
      }
    }
  }, [selectedIndex, isOpen]);

  return (
    <Popover open={isOpen} onOpenChange={(open) => !open && onClose()}>
      {targetElement && (
        <PopoverAnchor virtualRef={{ current: targetElement }} />
      )}
      <PopoverContent
        className='w-64 md:w-80 p-1 shadow-xl' // Start with this width, adjust if needed
        sideOffset={5}
        align='start'
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        {commands.length === 0 ? (
          <div className='p-2 text-sm text-muted-foreground text-center'>
            No commands found.
          </div>
        ) : (
          <div ref={listRef} className='max-h-60 overflow-y-auto'>
            {commands.map((command, index) => (
              <div
                key={command.id}
                role='button'
                onClick={() => onSelectCommand(command.id)}
                onMouseEnter={() => onHoverIndex(index)} // Update index on hover
                className={cn(
                  'block w-full text-left px-2 py-1.5 rounded-sm cursor-pointer',
                  index === selectedIndex
                    ? 'bg-accent text-accent-foreground' // Highlight selected
                    : 'hover:bg-accent/50'
                )}
              >
                <div className='text-sm font-medium'>{command.name}</div>
                {command.description && (
                  <div className='text-xs text-muted-foreground mt-0.5'>
                    {command.description}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
